import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const SellerPrivacyPolicy = () => {
  return (
    <div className="p-4 space-y-3 text-sm text-[#404040] leading-6">
      <p>
        <strong>Effective Date:</strong> 01 Aug 2026
        <br />
        <strong>Last Updated:</strong> 01 Aug 2026
      </p>
      <p>
        This Privacy Policy explains how{" "}
        <strong>Fluxart Solutions Private Limited</strong> (&quot;we&quot;,
        &quot;us&quot;, &quot;our&quot;) collects, uses, shares, and protects
        personal data and business-related information of sellers/merchants
        using <strong>Mumin&#39;s Mart</strong>.
      </p>
      <p>
        By registering as a seller, you agree to this Privacy Policy and the
        Seller Terms and Conditions.
      </p>
      <h2 className="font-semibold">1. Scope</h2>
      <p>
        This policy applies to personal data and associated business information
        collected from individual sellers, authorized representatives,
        beneficial owners, and employees/agents of seller entities where
        relevant.
      </p>
      <h2 className="font-semibold">2. Data We Collect</h2>
      <p>We may collect:</p>
      <ul>
        <li>Identity and contact data: name, phone, email, designation.</li>
        <li>
          Business data: legal entity name, GSTIN, PAN, CIN/LLPIN, registered
          address, bank details, brand/store details.
        </li>
        <li>
          KYC/verification data: government-issued documents, proofs of address,
          authorized signatory details, beneficial owner details (as
          applicable).
        </li>
        <li>
          Commercial data: product listings, pricing, inventory, sales
          performance, commissions, disputes, returns.
        </li>
        <li>
          Tax and finance data: invoicing data, settlement reports, withholding
          records.
        </li>
        <li>
          Technical and usage data: device details, IP address, logs,
          browser/app metadata.
        </li>
        <li>
          Communication data: support tickets, compliance notices, grievances.
        </li>
      </ul>
      <h2 className="font-semibold">3. Sources of Data</h2>
      <p>We collect data:</p>
      <ul>
        <li>Directly from sellers during onboarding and operations.</li>
        <li>
          From payment partners, KYC providers, logistics partners, and publicly
          available regulatory sources.
        </li>
        <li>Through usage of seller portals, dashboards, and APIs.</li>
      </ul>
      <h2 className="font-semibold">4. Purposes of Processing</h2>
      <p>We process seller data to:</p>
      <ul>
        <li>Onboard and verify sellers.</li>
        <li>Enable listing, sale, delivery, and after-sales operations.</li>
        <li>
          Process settlements, fees, commissions, chargebacks, and refunds.
        </li>
        <li>Comply with tax, accounting, anti-fraud, and legal obligations.</li>
        <li>
          Detect policy violations, abuse, prohibited goods/services, and
          security incidents.
        </li>
        <li>Provide support, analytics, and performance insights.</li>
        <li>Communicate operational, legal, and product updates.</li>
      </ul>
      <p>
        Where legally required, we obtain consent before specific processing
        activities.
      </p>
      <h2 className="font-semibold">5. Sharing and Disclosure</h2>
      <p>We may share seller data with:</p>
      <ul>
        <li>
          Buyers/customers as needed for order fulfillment (for example, seller
          display name, support contacts, shipping origin).
        </li>
        <li>Payment gateways, banks, and settlement partners.</li>
        <li>Logistics/courier partners.</li>
        <li>KYC, identity verification, and fraud prevention vendors.</li>
        <li>Tax, audit, legal, and compliance advisors.</li>
        <li>Government and regulatory authorities where legally required.</li>
        <li>
          Affiliates and service providers under confidentiality and data
          protection obligations.
        </li>
      </ul>
      <h2 className="font-semibold">6. Cross-Border Processing</h2>
      <p>
        Data may be processed outside India through approved infrastructure or
        vendors, subject to Indian law and contractual safeguards.
      </p>
      <h2 className="font-semibold">7. Retention</h2>
      <p>Seller data is retained as long as required for:</p>
      <ul>
        <li>Contract performance and operational needs.</li>
        <li>Statutory records (including tax and accounting).</li>
        <li>Litigation defense, investigations, and dispute management.</li>
      </ul>
      <p>After retention periods, data is securely deleted or anonymized.</p>
      <h2 className="font-semibold">8. Security</h2>
      <p>
        We follow reasonable security practices and procedures to safeguard
        seller data, including access controls, encryption measures where
        appropriate, and secure storage practices.
      </p>
      <h2 className="font-semibold">9. Seller Rights</h2>
      <p>Subject to law, you may request:</p>
      <ul>
        <li>Access/summary of personal data processed.</li>
        <li>Correction or updating of inaccurate/incomplete data.</li>
        <li>Erasure of personal data where legally permissible.</li>
        <li>Withdrawal of consent (where processing is consent-based).</li>
        <li>Grievance redressal through our designated officer.</li>
      </ul>
      <p>Contact: privacy@muminsmart.com</p>
      <h2 className="font-semibold">10. Third-Party Services</h2>
      <p>
        Our Platform may integrate third-party tools/services. Their processing
        is governed by their own terms/policies. Sellers should review those
        policies independently.
      </p>
      <h2 className="font-semibold">11. Grievance Officer</h2>
      <ul>
        <li>Name: Huzefa</li>
        <li>Email: support@muminsmart.com</li>
        <li>
          Address: A78 Hasanji Nagar, Near Emerald Heights School, Indore -
          453331
        </li>
      </ul>
      <p>We address grievances within legally prescribed timelines.</p>
      <h2 className="font-semibold">12. Policy Updates</h2>
      <p>
        We may revise this policy periodically. Updated versions will be posted
        on the Platform. Continued use of seller services after updates
        indicates acceptance.
      </p>
      <h2 className="font-semibold">13. Governing Law</h2>
      <p>
        This policy is governed by Indian law, with jurisdiction at courts in
        Indore, Madhya Pradesh, India, subject to mandatory legal provisions.
      </p>
    </div>
  );
};

const SellerPrivacyPolicyDialog = () => {
  return (
    <Dialog>
      <DialogTrigger>
        <button type="button" className="font-semibold text-primary underline">
          Privacy Policy
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[85dvh] rounded-3xl">
        <DialogHeader>
          <DialogTitle>Privacy Policy</DialogTitle>
        </DialogHeader>
        <div className="no-scrollbar max-h-[50vh] overflow-y-auto">
          <SellerPrivacyPolicy />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellerPrivacyPolicyDialog;
